// ===== Per-network verification rules =====
//
// Check items come from each network's own published requirements, never from
// what convert-core.js happens to emit — checking our converter against itself
// would pass by construction and catch none of its bugs. Where a genuine
// third-party Luna export contradicts the documentation, the real build wins:
// a rule that fails shipping creatives is a broken rule.
//
//   googleads   Google Ads 고객센터 · HTML5 플레이어블 사양
//               (support.google.com/google-ads/answer/9981650)
//               대조: DEBUG_GOOGLE/googleAds*.html (정식 Luna Google 빌드)
//   applovin    AppLovin 지원센터 · Creative specs & guidelines
//               대조: DEBUG_APPLOVIN/applovin*.html (정식 Luna AppLovin 빌드)
//   unityads    Unity Grow · Playable asset specifications
//               대조: DEBUG_*/original.html (정식 Luna unityads 빌드; 7개 폴더 동일 파일)
//   mintegral   Mintegral 헬프센터 · Creative Asset Specifications
//               대조: DEBUG_MINTEGRAL/mintegral*.html (정식 Luna Mintegral 빌드 3개)
//   smadex      Smadex Playables 규격 시트 (DEBUG_SMADEX/spec.png)
//               참조 빌드는 없지만 규격 시트 자체가 1차 자료입니다.
//   meta        Meta 비즈니스 헬프센터 · Specifications for playable ads
//               참조 빌드 없음(DEBUG_META에는 변환 입력만 있습니다). 다만
//               FbPlayableAd 표면 자체는 모든 Luna 빌드에 들어 있고
//               adNetwork==="facebook" 으로 게이트되어 있어, API 형태는
//               타사 빌드에서 확인됩니다.
//   moloco      Moloco Ads 헬프센터 · Playable and IEC creative guide
//               대조: DEBUG_MOLOCO/moloco{,2}.html (정식 Moloco 타겟 빌드,
//               각각 MOLOCO_SDK_MAX / INMOBI 익스체인지로 송출된 딜리버리).
//               moloco3.html은 Moloco가 아니라 또 다른 unityads 빌드입니다.
//   ironsource  Unity Grow / ironSource 익스체인지 MRAID 사양
//               대조: DEBUG_IRONSOURCE/ironsource*.html — ironSource에 실제
//               투입된 소재 3개. ironSource를 타겟으로 빌드된 것이 아니라
//               (각각 aplvads / unityads / moloco 타겟) ironSource가 받아
//               실행한 것들이라, 사양 충족의 근거는 되지만 타게팅 근거는
//               되지 않습니다. 그래서 이 네트워크에는 타게팅 검사가 없습니다.
//
// Pure functions only — no DOM construction. `ctx` is the parsed build.

// The only remote origins Google permits; everything else must be a local
// relative path inside the ZIP.
const GOOGLE_ALLOWED_HOSTS = [
  'tpc.googlesyndication.com',   // exitapi.js
  'ajax.googleapis.com',         // Google-hosted jQuery / Greensock / CreateJS
  'fonts.googleapis.com',
  'fonts.gstatic.com',
];

const EXITAPI_SRC = 'https://tpc.googlesyndication.com/pagead/gadgets/html5/api/exitapi.js';

const pass = (note) => ({ status: 'pass', note });
const fail = (note) => ({ status: 'fail', note });
const warn = (note) => ({ status: 'warn', note });

// Hosts actually fetched by the markup — read off resource attributes rather
// than by scanning the source for "http". A genuine Luna Google build mentions
// mrdoob.github.io, console.re and docs.lunalabs.io in comments and debug
// strings while fetching none of them; a text scan would call that a violation.
const RESOURCE_ATTRS = [
  ['script', 'src'], ['link', 'href'], ['img', 'src'], ['image', 'href'],
  ['source', 'src'], ['audio', 'src'], ['video', 'src'], ['iframe', 'src'],
];

// Every markup resource reference, with its raw value — fetchedHosts() only
// reports absolute http(s) hosts, and some rules need to see the scheme itself.
export function resourceRefs(doc) {
  const out = [];
  for (const [tag, attr] of RESOURCE_ATTRS) {
    for (const el of doc.querySelectorAll(tag + '[' + attr + ']')) {
      const v = (el.getAttribute(attr) || '').trim();
      if (v && !/^(?:data:|blob:|#)/i.test(v)) out.push(v);
    }
  }
  return out;
}

export function fetchedHosts(doc) {
  const hosts = new Set();
  for (const [tag, attr] of RESOURCE_ATTRS) {
    for (const el of doc.querySelectorAll(tag + '[' + attr + ']')) {
      const m = /^\s*https?:\/\/([a-z0-9.-]+)/i.exec(el.getAttribute(attr) || '');
      if (m) hosts.add(m[1].toLowerCase());   // data:/blob:/relative all skip
    }
  }
  return [...hosts];
}

export const RULES = {
  googleads: {
    label: 'Google Ads',
    source: 'Google Ads 고객센터 · HTML5 플레이어블 사양',
    static: [
      {
        id: 'size',
        label: 'ZIP 5 MB 이하',
        spec: 'Upload your asset as a .ZIP file with a maximum size of 5MB',
        run: ({ size }) => size <= 5 * 1024 * 1024
          ? pass((size / 1048576).toFixed(2) + ' MB')
          : fail((size / 1048576).toFixed(2) + ' MB — 5 MB 초과'),
      },
      {
        id: 'filecount',
        label: '파일 512개 이하',
        spec: 'no more than 512 files within the .ZIP',
        run: ({ files }) => files.length <= 512
          ? pass(files.length + '개')
          : fail(files.length + '개 — 512개 초과'),
      },
      {
        id: 'doctype',
        label: 'DOCTYPE 선언',
        spec: 'Assets must include a <!DOCTYPE html> declaration',
        run: ({ html }) => /^\s*<!DOCTYPE\s+html/i.test(html)
          ? pass() : fail('문서 최상단에 <!DOCTYPE html> 선언이 없습니다'),
      },
      {
        id: 'htmlbody',
        label: 'html · body 태그',
        spec: 'Assets must include <html> and <body> tags',
        run: ({ doc }) => doc.documentElement && doc.body
          ? pass() : fail('<html> 또는 <body> 태그가 없습니다'),
      },
      {
        id: 'orientation',
        label: 'ad.orientation 메타태그',
        spec: '<meta name="ad.orientation" content="portrait | landscape | portrait,landscape">',
        run: ({ doc }) => {
          const meta = doc.querySelector('head meta[name="ad.orientation" i]');
          if (!meta) return fail('head에 <meta name="ad.orientation"> 가 없습니다');
          const v = (meta.getAttribute('content') || '').trim().toLowerCase().replace(/\s/g, '');
          return ['portrait', 'landscape', 'portrait,landscape', 'landscape,portrait'].includes(v)
            ? pass(v)
            : fail('content="' + v + '" — portrait / landscape / portrait,landscape 중 하나여야 합니다');
        },
      },
      {
        id: 'charset',
        label: 'UTF-8 인코딩 선언',
        spec: 'UTF-8 must be used for non-ASCII characters',
        run: ({ doc }) => {
          const cs = doc.querySelector('head meta[charset]')?.getAttribute('charset')?.trim().toLowerCase();
          if (cs === 'utf-8') return pass('meta charset');
          const eq = doc.querySelector('head meta[http-equiv="Content-Type" i]');
          return /utf-8/i.test(eq?.getAttribute('content') || '')
            ? pass('http-equiv Content-Type') : fail('head에 UTF-8 선언이 없습니다');
        },
      },
      {
        id: 'exitapi-available',
        label: 'ExitApi 제공',
        spec: '<script src="' + EXITAPI_SRC + '"> 또는 동등한 인라인 구현',
        // Google documents the hosted script tag, but a genuine Luna Google
        // export ships the same API inlined — it registers "ExitApi.exit",
        // "ExitApi.close" and "ExitApi.delayCloseButton" itself — and passes
        // review. Demanding the tag would fail real, shipping builds, so accept
        // either and let the runtime check settle whether the API is really there.
        run: ({ doc, code }) => {
          const tag = [...doc.querySelectorAll('script[src]')]
            .some(s => (s.getAttribute('src') || '').includes('/html5/api/exitapi.js'));
          if (tag) return pass('exitapi.js 스크립트 태그');
          return /["']ExitApi\.(?:exit|close)["']|ExitApi\s*=[^=]/.test(code)
            ? pass('인라인 구현')
            : fail('ExitApi를 제공하는 스크립트 태그도 인라인 구현도 없습니다');
        },
      },
      {
        id: 'exitapi-call',
        label: 'ExitApi.exit() 호출 연결',
        spec: 'Invoke ExitApi.exit() for final URL navigation',
        run: ({ html }) => /ExitApi\s*(?:\.|\[\s*["'])\s*exit/.test(html)
          ? pass() : fail('코드에서 ExitApi.exit() 호출을 찾을 수 없습니다'),
      },
      {
        id: 'local-assets',
        label: '에셋 로컬 경로 사용',
        spec: 'All creative assets must be local relative paths, except Google-approved hosted libraries',
        run: ({ doc }) => {
          const bad = fetchedHosts(doc).filter(h =>
            !GOOGLE_ALLOWED_HOSTS.some(a => h === a || h.endsWith('.' + a)));
          return bad.length === 0
            ? pass('승인된 호스트만 참조')
            : fail('승인 목록 밖 호스트: ' + bad.join(', '));
        },
      },
    ],

    // Watched at runtime by the injected probe. `hooks` name the SDK entry
    // points to wrap; the checklist items are resolved from what the build
    // actually does while it plays, not from what its source contains.
    hooks: [{ id: 'cta', global: 'ExitApi', method: 'exit' }],
    runtime: [
      {
        id: 'exitapi-live',
        label: 'ExitApi 실제 사용 가능',
        spec: 'window.ExitApi.exit 가 로드 후 함수로 존재',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 ExitApi.exit() 발생',
        spec: 'Invoke ExitApi.exit() for final URL navigation',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
        needsPlay: true,
      },
      {
        id: 'audio',
        label: '사용자 입력 전 무음',
        spec: 'Sound should not be enabled before the user interacts with a Playable ad',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  applovin: {
    label: 'AppLovin',
    source: 'AppLovin 지원센터 · Creative specs & guidelines',
    static: [
      {
        id: 'size',
        label: '5 MB 이하',
        spec: 'Each ad file must be 5 MB or smaller.',
        run: ({ size }) => size <= 5 * 1024 * 1024
          ? pass((size / 1048576).toFixed(2) + ' MB')
          : fail((size / 1048576).toFixed(2) + ' MB — 5 MB 초과'),
      },
      {
        id: 'single-file',
        label: '단일 HTML 파일',
        spec: 'Each ad must be a single HTML file.',
        // AppLovin takes one document, not an archive of parts. A ZIP holding
        // exactly one HTML is still that one document, just wrapped.
        run: ({ kind, files, entryPath }) => {
          if (kind !== 'zip') return pass('단일 HTML');
          return files.length === 1
            ? pass('ZIP 내 ' + entryPath + ' 단일 파일')
            : fail(files.length + '개 파일 — 단일 HTML 하나여야 합니다');
        },
      },
      {
        id: 'no-external',
        label: '외부 리소스 없음',
        spec: 'All resources must be embedded in either base64 or base122 format; external resources are prohibited.',
        // No allowlist here, unlike Google — AppLovin permits no remote origin
        // at all. Read off resource attributes: the real builds name store URLs
        // and dev-tool hosts in JS strings while fetching none of them.
        run: ({ doc }) => {
          const hosts = fetchedHosts(doc);
          return hosts.length === 0
            ? pass('모든 리소스 인라인')
            : fail('외부 리소스 참조: ' + hosts.join(', '));
        },
      },
      {
        id: 'mraid-open',
        label: 'CTA가 mraid.open() 사용',
        spec: 'Use mraid.open() for click-through actions.',
        run: ({ html }) => /mraid\s*(?:\.|\[\s*["'])\s*open/.test(html)
          ? pass() : fail('코드에서 mraid.open() 호출을 찾을 수 없습니다'),
      },
      {
        id: 'mraid-ready',
        label: 'MRAID ready 대기',
        spec: "Wait for the ready event (or confirm mraid.getState() is not 'loading') before calling any MRAID APIs.",
        // The genuine Luna AppLovin exports do exactly this:
        //   "loading" === mraid.getState() ? mraid.addEventListener("ready", r) : r()
        run: ({ code }) => {
          const waitsForEvent = /addEventListener\s*\(\s*["']ready["']/.test(code);
          const checksState = /mraid\s*\.\s*getState\s*\(\s*\)/.test(code) && /["']loading["']/.test(code);
          if (waitsForEvent && checksState) return pass('ready 이벤트 + getState 확인');
          if (waitsForEvent) return pass('ready 이벤트 대기');
          if (checksState) return pass('getState() 확인');
          return fail("ready 이벤트도 getState() 확인도 없이 MRAID를 호출합니다");
        },
      },
      {
        id: 'mraid-guard',
        label: 'MRAID 미주입 시 폴백',
        spec: 'MRAID is host-injected; an unguarded call throws when the container is absent.',
        // Not in the written spec — taken from the shipping builds, which guard
        // every entry point with `"undefined" != typeof mraid` and fall back to
        // window.open. Reported, not failed: it is robustness, not a rejection
        // reason.
        run: ({ html }) => /typeof\s+mraid|window\s*\.\s*mraid|["']mraid["']\s+in\s+window/.test(html)
          ? pass('가드 확인')
          : warn('mraid 존재 확인 없이 호출합니다 — 컨테이너가 없으면 예외가 납니다'),
      },
    ],

    // Targeting checks answer a different question from the spec checks above:
    // not "would AppLovin reject this file" but "did the AppLovin conversion
    // actually happen". They have to, because an untargeted Luna build already
    // satisfies every published AppLovin requirement — its stock CTA is already
    // mraid.open — so the spec checks alone pass a file that was never converted.
    //
    // Source is the genuine Luna AppLovin exports, which all carry one ~3.2 KB
    // trailing script. Nothing here appears in AppLovin's written spec, so a
    // miss is a warning, not a rejection: the file is still servable, it just
    // reports nothing to AppLovin.
    targeting: [
      {
        id: 'al-event-enum',
        label: '이벤트 상수 정의',
        spec: 'window.APPLOVIN_ANALYTICS_EVENTS = { LOADING, LOADED, DISPLAYED, CTA_CLICKED, ENDCARD_SHOWN, CHALLENGE_* }',
        run: ({ html }) => /APPLOVIN_ANALYTICS_EVENTS\s*=/.test(html) && /CHALLENGE_PASS_25/.test(html)
          ? pass('12종 확인') : warn('APPLOVIN_ANALYTICS_EVENTS 정의가 없습니다'),
      },
      {
        id: 'al-sdk-bridge',
        label: 'AppLovin SDK 브리지',
        spec: 'window.callAnalyticsEvent = n => window.ALPlayableAnalytics.trackEvent(n)',
        // The single egress point to the host SDK; without it every event below
        // is computed and thrown away.
        run: ({ html }) => /ALPlayableAnalytics\s*\.\s*trackEvent/.test(html)
          ? pass('ALPlayableAnalytics.trackEvent 연결')
          : warn('ALPlayableAnalytics로 내보내는 경로가 없습니다 — 지표가 잡히지 않습니다'),
      },
      {
        id: 'al-lifecycle',
        label: '라이프사이클 이벤트 매핑',
        spec: 'luna:start→LOADING, luna:started→LOADED, luna:postrender→DISPLAYED, luna:ended→ENDCARD_SHOWN',
        run: ({ code }) => {
          const hits = ['luna:start', 'luna:started', 'luna:postrender', 'luna:ended']
            .filter(ev => code.includes('"' + ev + '"') || code.includes("'" + ev + "'"));
          const mapped = /DISPLAYED/.test(code) && /ENDCARD_SHOWN/.test(code);
          return hits.length === 4 && mapped
            ? pass('4종 연결') : warn(`노출·플레이 지표 연결이 없습니다 (${hits.length}/4)`);
        },
      },
      {
        id: 'al-challenge-log',
        label: '게임 진행도 보고',
        spec: 'Luna.Unity.Analytics.Applovin.LogChallenge{Started,Failed,Retry,Pass25,Pass50,Pass75,Solved}',
        run: ({ html }) => {
          const n = (html.match(/Analytics\s*\.\s*Applovin\s*\.\s*LogChallenge/g) || []).length;
          return n >= 7 ? pass(`${n}종 확인`)
            : warn(n ? `${n}/7종만 연결되었습니다` : '진행도 보고가 연결되지 않았습니다');
        },
      },
      {
        id: 'al-cta-event',
        label: 'CTA 클릭 보고',
        spec: 'InstallFullGame 이 mraid.open 앞에서 CTA_CLICKED 를 전송',
        // The conversion overrides the stock CTA to report before it exits;
        // check the marker sits inside an InstallFullGame body, not just anywhere.
        run: ({ html }) => {
          const re = /InstallFullGame\s*=\s*function[\s\S]{0,240}?CTA_CLICKED/;
          if (re.test(html)) return pass('CTA_CLICKED 전송 확인');
          return /CTA_CLICKED/.test(html)
            ? warn('CTA_CLICKED 상수는 있으나 CTA에 연결되지 않았습니다')
            : warn('CTA 클릭이 AppLovin에 보고되지 않습니다');
        },
      },
      {
        id: 'al-force-mute',
        label: '시작 시 강제 음소거',
        spec: 'luna:starting → audioVolumeToggle(true), 첫 입력에 해제',
        // Both `audioVolumeToggle` and `luna:starting` already exist separately in
        // the base Luna runtime, so testing for them individually passes an
        // unconverted build. What the conversion adds is the wiring between them.
        run: ({ code }) => /addEventListener\(\s*["']luna:starting["'][\s\S]{0,160}?audioVolumeToggle/.test(code)
          ? pass('시작 음소거 + 첫 입력 해제')
          : warn('시작 시 음소거 처리가 없습니다'),
      },
      {
        id: 'al-resize-poll',
        label: '회전 대응 resize 폴링',
        spec: '300ms 간격으로 innerWidth/innerHeight 변화를 감시해 resize 이벤트 발생',
        // AppLovin's webview does not always fire resize on rotation; the real
        // exports synthesize it.
        run: ({ html }) => /new Event\(\s*["']resize["']\s*\)/.test(html) && /setInterval/.test(html)
          ? pass('폴링 확인')
          : warn('회전 시 레이아웃이 따라가지 않을 수 있습니다'),
      },
    ],

    // MRAID is never shipped by the creative — the ad SDK puts it on window.
    // Without the stub every guarded call takes its no-SDK fallback and none of
    // the integration is exercised.
    provide: ['mraid'],
    mraidVersion: '2.0',      // "HTML ads must support MRAID v2.0."
    hooks: [],
    runtime: [
      {
        id: 'mraid-use',
        label: 'MRAID 컨테이너 연동',
        spec: '빌드가 실제로 mraid API를 호출',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 mraid.open() 발생',
        spec: 'Use mraid.open() for click-through actions.',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'first-tap',
        label: '첫 탭에서 스토어로 보내지 않음',
        spec: 'Each ad must be playable and must not redirect to app stores on the first tap/click.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'audio',
        label: '사용자 입력 전 무음',
        spec: 'Audio must remain muted until the first user interaction.',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'no-network',
        label: '외부 네트워크 호출 없음',
        spec: 'External network calls are prohibited.',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  unityads: {
    label: 'Unity Ads',
    source: 'Unity Grow · Playable asset specifications',
    static: [
      {
        id: 'size',
        label: '5 MB 이하',
        spec: 'Maximum 5 MB.',
        run: ({ size }) => size <= 5 * 1024 * 1024
          ? pass((size / 1048576).toFixed(2) + ' MB')
          : fail((size / 1048576).toFixed(2) + ' MB — 5 MB 초과'),
      },
      {
        id: 'single-file',
        label: '단일 HTML 파일',
        spec: 'Contained in a single index.html file with no links to other files or folders.',
        run: ({ kind, files, entryPath }) => {
          if (kind !== 'zip') return pass('단일 HTML');
          return files.length === 1
            ? pass('ZIP 내 ' + entryPath + ' 단일 파일')
            : fail(files.length + '개 파일 — 다른 파일/폴더 링크 없이 하나여야 합니다');
        },
      },
      {
        id: 'inlined',
        label: '에셋 전부 인라인',
        spec: 'All assets must be inlined within the HTML file.',
        run: ({ doc }) => {
          const hosts = fetchedHosts(doc);
          return hosts.length === 0
            ? pass('외부 참조 없음')
            : fail('외부 리소스 참조: ' + hosts.join(', '));
        },
      },
      {
        id: 'minified',
        label: 'minify 적용',
        spec: 'File must be minified (optimized for machine-reading by removing empty spaces).',
        // Every genuine Luna export measures 0.06%-1.7% whitespace, so a 5%
        // ceiling clears them all while still catching a beautified document.
        run: ({ html }) => {
          const ws = (html.match(/[ \t\n\r]/g) || []).length / (html.length || 1);
          const pct = (ws * 100).toFixed(2) + '%';
          return ws < 0.05 ? pass('공백 ' + pct) : fail('공백 ' + pct + ' — minify되지 않았습니다');
        },
      },
      {
        id: 'mraid-open',
        label: 'CTA가 mraid.open() 사용',
        spec: 'Ad call-to-actions should link directly to the app store using mraid.open().',
        run: ({ html }) => /mraid\s*(?:\.|\[\s*["'])\s*open/.test(html)
          ? pass() : fail('코드에서 mraid.open() 호출을 찾을 수 없습니다'),
      },
      {
        id: 'viewable-gate',
        label: 'viewableChange 후 시작',
        spec: 'Ads should wait for the MRAID viewableChange event before starting playable content.',
        // Unity gates on viewableChange, not on `ready` the way AppLovin does.
        // The genuine build registers it and only starts once isViewable() and
        // getState() agree the ad is on screen.
        run: ({ code }) => {
          const registers = /addEventListener\(\s*["']viewableChange["']/.test(code);
          const checks = /isViewable\s*\(\s*\)/.test(code);
          if (registers && checks) return pass('viewableChange + isViewable 확인');
          if (registers) return pass('viewableChange 등록');
          return fail('viewableChange를 기다리지 않고 시작합니다');
        },
      },
    ],

    // Unity Ads is the source format — a Luna export already is a Unity Ads
    // build, so unlike AppLovin there is no SDK glue to look for. What is left
    // to confirm is that the build was cleaned for delivery, so these are all
    // warnings: neither is a Unity rejection reason.
    targetingLabel: '변환 확인',
    targeting: [
      {
        id: 'u-target-platform',
        label: 'targetPlatform 표시',
        spec: 'window.$environment.targetPlatform === "unityads"',
        run: ({ html }) => {
          const m = /targetPlatform\s*:\s*["']([a-z0-9_]+)["']/i.exec(html);
          if (!m) return warn('targetPlatform을 찾을 수 없습니다');
          return m[1].toLowerCase() === 'unityads'
            ? pass('"' + m[1] + '"')
            : warn('"' + m[1] + '" — 다른 네트워크 대상으로 표시되어 있습니다');
        },
      },
      {
        id: 'u-no-remote',
        label: '원격 참조 잔여 없음',
        spec: 'All assets must be inlined. Ads should not need network requests (XHR).',
        // The raw Luna export carries remote script injectors (console.re,
        // spectorjs, stats.js) and remote asset overrides. They sit in JS
        // strings rather than markup attributes, so the `inlined` rule above —
        // which reads resource attributes — cannot see them. Flag-gated and so
        // normally dormant, which is why this warns instead of failing.
        run: ({ html }) => {
          const found = ['console.re', 'cdn.jsdelivr.net', 'mrdoob.github.io', 'assets.lunalabs.io']
            .filter(h => html.includes(h));
          return found.length === 0
            ? pass('원격 참조 없음')
            : warn('원격 참조가 남아있습니다: ' + found.join(', '));
        },
      },
    ],

    provide: ['mraid'],
    mraidVersion: '3.0',      // "compliant with MRAID 3.0 specifications"
    hooks: [],
    runtime: [
      {
        id: 'mraid-use',
        label: 'MRAID 컨테이너 연동',
        spec: 'MRAID is injected by the Unity Ads webview.',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 mraid.open() 발생',
        spec: 'Ad call-to-actions should link directly to the app store using mraid.open().',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'first-tap',
        label: '자동 스토어 이동 없음',
        spec: 'Ads should not automatically redirect to the app store without explicit user action.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'no-network',
        label: '네트워크 요청 없음',
        spec: 'Ads should not need network requests (XHR), but analytic calls without personal data may be permitted.',
        // Unity permits analytics calls, unlike AppLovin which prohibits all of
        // them, so a request here is worth surfacing rather than failing.
        severity: 'warn',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  mintegral: {
    label: 'Mintegral',
    source: 'Mintegral 헬프센터 · Creative Asset Specifications',
    static: [
      {
        id: 'size',
        label: '5 MB 이하',
        spec: 'Maximum file size: 5 MB.',
        run: ({ size }) => size <= 5 * 1024 * 1024
          ? pass((size / 1048576).toFixed(2) + ' MB')
          : fail((size / 1048576).toFixed(2) + ' MB — 5 MB 초과'),
      },
      {
        id: 'single-file',
        label: '단일 HTML 파일',
        spec: 'HTML (single file). Zip file, assets folder and HTML file should be named identically.',
        // Mintegral takes a ZIP with resources too, but then the archive, its
        // folder and the document must share a name.
        run: ({ kind, files, entryPath, name }) => {
          if (kind !== 'zip') return pass('단일 HTML');
          if (files.length === 1) return pass('ZIP 내 ' + entryPath + ' 단일 파일');
          const stem = (name || '').replace(/\.zip$/i, '');
          const doc = entryPath.split('/').pop().replace(/\.html?$/i, '');
          return stem && doc === stem
            ? pass(files.length + '개 파일 · 이름 규칙 일치')
            : warn(`ZIP "${stem}" 과 문서 "${doc}" 이름이 다릅니다 — 동일해야 합니다`);
        },
      },
      {
        id: 'inlined',
        label: '외부 리소스 참조 없음',
        spec: '플레이어블은 자기완결이어야 합니다.',
        run: ({ doc }) => {
          const hosts = fetchedHosts(doc);
          return hosts.length === 0
            ? pass('외부 참조 없음')
            : fail('외부 리소스 참조: ' + hosts.join(', '));
        },
      },
    ],

    // Mintegral shares nothing with the MRAID networks: the reference builds
    // carry no mraid at all, and the creative talks to the player through a
    // handful of window globals instead. The split, read off the genuine
    // exports, is that the creative defines gameStart/gameClose and calls
    // gameReady/gameEnd/install, while the player supplies the other half.
    //
    // Note what is deliberately NOT checked here. The genuine files also carry
    // an 83.6 KB Mintegral SDK bundle (MW_CONFIG, WindVane, MTG_OFFLINE_PACKAGE,
    // the rayjump loader) prepended ahead of the document — but that is the
    // platform wrapping the creative after upload, not something a creative
    // tool produces. Requiring it would fail every build before it ships.
    targetingLabel: '타게팅 검사',
    targeting: [
      {
        id: 'mtg-no-mraid',
        label: 'MRAID 제거',
        spec: '정식 Mintegral 빌드는 mraid 호출이 0건입니다.',
        run: ({ html }) => {
          const n = (html.match(/\bmraid\b/g) || []).length;
          return n === 0 ? pass('mraid 호출 없음')
            : warn(`mraid 참조 ${n}건 — Mintegral 플레이어는 MRAID를 주입하지 않습니다`);
        },
      },
      {
        id: 'mtg-cta',
        label: 'CTA가 window.install() 호출',
        spec: 'InstallFullGame = function(){ window.pi.logCta(), window.install && window.install() }',
        // The store URL leaves the creative entirely — Mintegral holds it in
        // MW_CONFIG.store_url — so a CTA still carrying iosLink/androidLink or
        // window.open has not been converted.
        run: ({ html }) => {
          // Wide enough to span the unconverted body too (~340 chars with the
          // UA sniff and store links), so a build that was not converted reports
          // what it still carries instead of "not found". Every definition is
          // checked, not just the first: newer Luna exports re-assign
          // InstallFullGame from an analytics block registered later, and that
          // later assignment is the one the CTA actually runs.
          const defs = [...html.matchAll(/InstallFullGame\s*=\s*function[\s\S]{0,600}?\}/g)].map(m => m[0]);
          if (!defs.length) return warn('InstallFullGame 정의를 찾을 수 없습니다');
          const bad = defs.filter(d => !/window\.install/.test(d));
          if (bad.length) return warn(`InstallFullGame 정의 ${defs.length}개 중 ${bad.length}개가 window.install() 을 호출하지 않습니다`);
          return defs.some(d => /iosLink|androidLink|window\.open/.test(d))
            ? warn('CTA에 스토어 URL 로직이 남아있습니다 — Mintegral은 MW_CONFIG.store_url 을 씁니다')
            : pass(defs.length > 1 ? `window.install() 연결 (정의 ${defs.length}개 모두)` : 'window.install() 연결');
        },
      },
      {
        id: 'mtg-gameend',
        label: '종료 시 gameEnd 보고',
        spec: 'luna:ended → window.gameEnd && window.gameEnd()',
        run: ({ code }) => /addEventListener\(\s*["']luna:ended["'][\s\S]{0,120}?gameEnd/.test(code)
          ? pass('luna:ended → gameEnd()')
          : warn('게임 종료가 Mintegral에 보고되지 않습니다'),
      },
      {
        id: 'mtg-gameready',
        label: '준비 완료 시 gameReady 보고',
        spec: 'luna:started → window.gameReady && window.gameReady()',
        run: ({ code }) => /addEventListener\(\s*["']luna:started["'][\s\S]{0,120}?gameReady/.test(code)
          ? pass('luna:started → gameReady()')
          : warn('준비 완료가 보고되지 않아 플레이어가 게임을 시작시키지 못합니다'),
      },
      {
        id: 'mtg-host-hooks',
        label: 'gameStart · gameClose 정의',
        spec: 'window.gameStart / window.gameClose — 플레이어가 호출하는 진입점',
        run: ({ html }) => {
          const has = (n) => new RegExp('window\\.' + n + '\\s*=\\s*function').test(html);
          const missing = ['gameStart', 'gameClose'].filter(n => !has(n));
          return missing.length === 0
            ? pass('둘 다 정의됨')
            : warn(missing.join(' · ') + ' 정의가 없습니다 — 플레이어가 제어할 수 없습니다');
        },
      },
      {
        id: 'mtg-adata',
        label: '리워드 여부 연동',
        spec: 'pi.injectAdData → window.MW_INIT.isRewardAds()',
        run: ({ html }) => /MW_INIT[\s\S]{0,80}?isRewardAds/.test(html)
          ? pass('MW_INIT.isRewardAds() 연결')
          : warn('리워드 여부를 읽지 않습니다'),
      },
      {
        id: 'mtg-console',
        label: 'console 메서드 재정의 없음',
        spec: 'PlayTurbo 검수: "Do not override console methods" — Code exception으로 반려됩니다.',
        // Luna's remote-debugging wrapper reassigns console[t] for six methods.
        // The genuine Mintegral builds carry none, SDK bundle included.
        run: ({ html }) => {
          const n = (html.match(/\bconsole\s*(?:\.\s*(?:log|warn|error|info|debug|trace)|\[[^\]\n]{1,40}\])\s*=(?!=)/g) || []).length;
          return n === 0 ? pass('재정의 없음')
            : fail(`console 재정의 ${n}건 — Luna 원격 디버깅 래퍼가 남아 있습니다`);
        },
      },
      {
        id: 'mtg-base64',
        label: '페이로드 base64 인코딩',
        spec: '정식 Mintegral 빌드의 압축 페이로드는 base64입니다 (base122 아님).',
        // base122 packs bytes into code units above 0x7F; base64 never exceeds
        // ASCII. Checking the payload strings themselves avoids tripping on the
        // vendor bundle's Chinese comments.
        run: ({ html }) => {
          let checked = 0, high = 0;
          for (const m of html.matchAll(/decompress(?:ArrayBuffer|String)\(\s*"([^"]*)"/g)) {
            checked++;
            for (let i = 0; i < m[1].length; i++) {
              if (m[1].charCodeAt(i) > 127) { high++; break; }
            }
          }
          if (!checked) return pass('압축 페이로드 없음');
          return high === 0
            ? pass(`${checked}개 페이로드 모두 ASCII`)
            : warn(`${high}/${checked}개 페이로드가 base122입니다 — PlayTurbo WebView에서 깨질 수 있습니다`);
        },
      },
    ],

    provide: ['mintegral'],
    hooks: [],
    runtime: [
      {
        id: 'mtg-gameready-live',
        label: '플레이어에 준비 완료 보고',
        spec: '빌드가 실행 중 window.gameReady() 를 호출',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'mtg-gamestart-live',
        label: '플레이어 신호로 게임 시작',
        spec: '플레이어가 gameReady 이후 window.gameStart() 로 게임을 시작',
        hint: '준비 완료 보고 직후 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 window.install() 발생',
        spec: 'InstallFullGame → window.install()',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'first-tap',
        label: '자동 스토어 이동 없음',
        spec: '첫 입력 전에 CTA가 발생하면 안 됩니다.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  ironsource: {
    label: 'IronSource',
    source: 'Unity Grow / ironSource 익스체인지 MRAID 사양',
    static: [
      {
        id: 'size',
        label: '크기 한도',
        spec: 'Unity Grow: under 5 MB. ironSource 익스체인지 MRAID: assets + code capped at 4 MB.',
        // The two published limits disagree, and a creative cannot know which
        // inventory path it will run through. All three creatives that actually
        // shipped on ironSource measure under 4 MB, so the lower figure is the
        // one to build to — over it is a warning, over 5 MB a failure.
        run: ({ size }) => {
          const mb = (size / 1048576).toFixed(2) + ' MB';
          if (size > 5 * 1024 * 1024) return fail(mb + ' — 5 MB 초과');
          if (size > 4 * 1024 * 1024) return warn(mb + ' — 익스체인지 한도 4 MB 초과 (Grow 기준 5 MB는 충족)');
          return pass(mb);
        },
      },
      {
        id: 'single-file',
        label: '단일 자기완결 HTML',
        spec: 'Single self-contained index.html — no linked files or folders, assets inlined.',
        run: ({ kind, files, entryPath }) => {
          if (kind !== 'zip') return pass('단일 HTML');
          return files.length === 1
            ? pass('ZIP 내 ' + entryPath + ' 단일 파일')
            : fail(files.length + '개 파일 — 링크된 파일/폴더 없이 하나여야 합니다');
        },
      },
      {
        id: 'external-https',
        label: '외부 참조는 절대 HTTPS',
        spec: 'Any unavoidable external reference must use an absolute HTTPS URL — no relative paths, no HTTP.',
        // Not a ban: the rule is about the scheme. One of the three creatives
        // that shipped on ironSource loads a remote tracker and 27 remote
        // images, all over https, so failing on the presence of an external
        // reference would contradict what the network actually accepts.
        run: ({ doc }) => {
          const refs = resourceRefs(doc);
          const bad = refs.filter(u => !/^https:\/\//i.test(u));
          if (!refs.length) return pass('외부 참조 없음');
          return bad.length === 0
            ? pass(refs.length + '건 모두 https')
            : fail(`https가 아닌 참조 ${bad.length}건: ${bad.slice(0, 2).join(', ')}`);
        },
      },
      {
        id: 'mraid-open',
        label: 'CTA가 mraid.open() 사용',
        spec: 'mraid.open(url), fired only on explicit user interaction — never an automatic redirect.',
        // DAPI is documented elsewhere for ironSource, but none of the three
        // shipped creatives uses it: dapi appears once, in a branch gated on an
        // adNetwork value that is never set. All three go through MRAID.
        run: ({ html }) => /mraid\s*(?:\.|\[\s*["'])\s*open/.test(html)
          ? pass() : fail('코드에서 mraid.open() 호출을 찾을 수 없습니다'),
      },
      {
        id: 'viewable-gate',
        label: 'viewableChange 후 시작',
        spec: 'Gate the start of interactive content on the MRAID viewableChange event.',
        run: ({ code }) => {
          const registers = /addEventListener\(\s*["']viewableChange["']/.test(code);
          const checks = /isViewable\s*\(\s*\)/.test(code);
          if (registers && checks) return pass('viewableChange + isViewable 확인');
          if (registers) return pass('viewableChange 등록');
          return fail('viewableChange를 기다리지 않고 시작합니다');
        },
      },
    ],

    // No targeting group. Every other network's targeting checks were derived
    // from genuine exports built for that network; the files here were built
    // for other targets and merely delivered on ironSource, so there is nothing
    // to derive an "was this converted" check from. Inventing one would produce
    // exactly the kind of check that passes without meaning anything.

    provide: ['mraid'],
    // Grow cites MRAID 3.0, the ironSource exchange spec cites 2.0. None of the
    // shipped creatives calls getVersion(), so nothing here depends on it; the
    // lower figure is reported since that is the exchange the creative meets.
    mraidVersion: '2.0',
    hooks: [],
    runtime: [
      {
        id: 'mraid-use',
        label: 'MRAID 컨테이너 연동',
        spec: '빌드가 실제로 mraid API를 호출',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 mraid.open() 발생',
        spec: 'mraid.open(url) on CTA tap.',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'first-tap',
        label: '자동 스토어 이동 없음',
        spec: 'Fired only on explicit user interaction — never an automatic redirect.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'no-network',
        label: '네트워크 요청 없음',
        spec: 'Avoid XHR/network calls.',
        // "Avoid", not "prohibit" — and a shipped creative does fetch. Surfaced
        // rather than failed.
        severity: 'warn',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  moloco: {
    label: 'Moloco',
    source: 'Moloco Ads 헬프센터 · Playable and IEC creative guide',
    static: [
      {
        id: 'size',
        label: '5 MB 이하',
        spec: 'under 5 MB.',
        run: ({ size }) => size <= 5 * 1024 * 1024
          ? pass((size / 1048576).toFixed(2) + ' MB')
          : fail((size / 1048576).toFixed(2) + ' MB — 5 MB 초과'),
      },
      {
        id: 'not-zip',
        label: 'ZIP 아님',
        spec: 'Ad file must not be compressed into .zip format for upload.',
        // The only network so far that refuses an archive outright.
        run: ({ kind }) => kind === 'zip'
          ? fail('ZIP으로는 업로드할 수 없습니다 — 단일 HTML이어야 합니다')
          : pass('단일 HTML'),
      },
      {
        id: 'no-mraid-js',
        label: 'mraid.js 미포함',
        spec: 'The standalone playable must not include mraid.js.',
        // The rule is about shipping the library, not about touching the object:
        // both genuine Moloco deliveries drive mraid.isViewable/open/
        // addEventListener heavily, and Moloco's own trampoline is what calls
        // them. Reading this as "no window.mraid" would fail Moloco's own wrapper.
        run: ({ doc, code }) => {
          const tag = [...doc.querySelectorAll('script[src]')]
            .some(el => /\bmraid(?:\.min)?\.js\b/i.test(el.getAttribute('src') || ''));
          if (tag) return fail('mraid.js 스크립트를 포함하고 있습니다');
          return /\bmraid(?:\.min)?\.js\b/i.test(code)
            ? warn('코드에 mraid.js 참조 문자열이 있습니다')
            : pass('라이브러리 미포함');
        },
      },
      {
        id: 'no-xhr',
        label: 'XMLHttpRequest 미사용',
        spec: 'No XMLHttpRequest.',
        run: ({ code }) => /\bXMLHttpRequest\b/.test(code)
          ? fail('XMLHttpRequest 사용이 발견되었습니다')
          : pass('사용 없음'),
      },
      {
        id: 'no-js-redirect',
        label: 'JS 리디렉트 없음',
        spec: "No JavaScript redirects anywhere in the ad file's assets.",
        run: ({ code }) => {
          const hits = code.match(/location\s*\.\s*(?:href\s*=|replace\s*\(|assign\s*\()/g) || [];
          return hits.length === 0
            ? pass('리디렉트 없음')
            : fail(`location 리디렉트 ${hits.length}건`);
        },
      },
      {
        id: 'no-external',
        label: '외부 참조 없음',
        spec: 'All image and sound assets must be data-URI-encoded; the creative must not make any external references.',
        // Warned, not failed. Both genuine Moloco deliveries carry remote assets
        // — 107 and 149 <img> preloads on assets-N.lunalabs.io, every inline*
        // flag off — so they cannot run offline, and one also loads Moloco's own
        // tracker over https. The guide governs what is uploaded; what gets
        // served evidently does not have to meet it.
        run: ({ doc }) => {
          const hosts = fetchedHosts(doc);
          return hosts.length === 0
            ? pass('외부 참조 없음')
            : warn(`외부 참조 ${hosts.length}개 호스트: ${hosts.slice(0, 3).join(', ')}${hosts.length > 3 ? ' …' : ''}`);
        },
      },
    ],

    targetingLabel: '타게팅 검사',
    targeting: [
      {
        id: 'mlc-target-platform',
        label: 'targetPlatform 표시',
        spec: 'window.$environment.targetPlatform === "moloco"',
        run: ({ code }) => {
          const m = /targetPlatform\s*:\s*["']([a-z0-9_]+)["']/i.exec(code);
          if (!m) return warn('targetPlatform을 찾을 수 없습니다');
          return m[1].toLowerCase() === 'moloco'
            ? pass('"' + m[1] + '"')
            : warn('"' + m[1] + '" — 다른 네트워크 대상으로 표시되어 있습니다');
        },
      },
      {
        id: 'mlc-trampoline',
        label: 'Moloco 트램폴린 삽입',
        spec: '추적 픽셀 · 탭 카운트 · CTA 재연결 블록',
        // 1,599 bytes, byte-identical across all three genuine deliveries.
        run: ({ code }) => /getElementById\(\s*["']trampoline["']\s*\)/.test(code)
          && /taps_for_redirection/.test(code)
          ? pass('트램폴린 블록 확인')
          : warn('Moloco 추적 블록이 없습니다'),
      },
      {
        id: 'mlc-fb-cta',
        label: 'FbPlayableAd.onCTAClick() 호출',
        spec: 'Calls FbPlayableAd.onCTAClick() with no parameters.',
        // Moloco's self-serve upload validator looks for the literal call
        // "FbPlayableAd.onCTAClick()" in the .html and rejects the upload
        // without it ("Add the JavaScript function "FbPlayableAd.onCTAClick()"
        // to your .html file and try again"). The genuine Luna-hosted
        // deliveries only *define* it as a no-op inside the trampoline — they
        // never passed through that validator — so a definition alone is not
        // enough and fails here.
        run: ({ code }) => {
          if (/FbPlayableAd\s*\.\s*onCTAClick\s*\(\s*\)/.test(code)) return pass('호출됨');
          if (/FbPlayableAd\s*\.\s*onCTAClick\s*=/.test(code))
            return fail('정의만 있고 호출이 없습니다 — Moloco 업로드 검사기가 거부합니다');
          return fail('FbPlayableAd.onCTAClick() 호출이 없습니다 — Moloco 업로드 검사기가 거부합니다');
        },
      },
      {
        id: 'mlc-cta-fallback',
        label: 'CTA 폴백 경로 유지',
        spec: '트램폴린이 초기화에 실패해도 스토어로 갈 수 있어야 합니다.',
        // This matters more than it looks — see mlc-trampoline-element below.
        run: ({ code }) => /InstallFullGame\s*=\s*function[\s\S]{0,600}?mraid\s*\.\s*open/.test(code)
          ? pass('mraid.open 폴백 확인')
          : warn('트램폴린이 죽으면 스토어 이동 경로가 없습니다'),
      },
      {
        id: 'mlc-trampoline-element',
        label: '트램폴린 파라미터 엘리먼트',
        spec: '트램폴린은 id="trampoline" 엘리먼트의 src 쿼리스트링에서 값을 읽습니다.',
        // Every genuine Moloco delivery examined — two here plus one found in
        // the ironSource folder, three independent exchanges — carries the block
        // but no element with that id, so its first statement throws and the
        // whole IIFE (tracking pixels, tap counter, CTA override) never
        // registers. Presumably the ad server templates the element in at final
        // serve; reported either way, because if it does not, all Moloco
        // tracking on the creative is silently dead.
        run: ({ code, doc }) => {
          const hasBlock = /getElementById\(\s*["']trampoline["']\s*\)/.test(code);
          if (!hasBlock) return pass('트램폴린 없음 — 해당 없음');
          const el = doc.getElementById && doc.getElementById('trampoline');
          return el || /id\s*=\s*\\?["']trampoline\\?["']/.test(code)
            ? pass('엘리먼트 확인')
            : warn('엘리먼트가 없어 트램폴린이 첫 줄에서 예외로 죽습니다 (송출 시 주입되는 것으로 보임)');
        },
      },
    ],

    provide: ['mraid'],
    mraidVersion: '2.0',
    hooks: [],
    runtime: [
      {
        id: 'mraid-use',
        label: 'MRAID 컨테이너 연동',
        spec: '빌드가 실제로 mraid API를 호출',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 mraid.open() 발생',
        spec: '트램폴린이 죽어도 기본 CTA가 스토어를 엽니다.',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'first-tap',
        label: '자동 스토어 이동 없음',
        spec: '첫 입력 전에 CTA가 발생하면 안 됩니다.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'no-network',
        label: '네트워크 요청 없음',
        spec: 'The creative must not make any external references.',
        severity: 'warn',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  meta: {
    label: 'Meta',
    source: 'Meta 비즈니스 헬프센터 · Specifications for playable ads',
    static: [
      {
        id: 'size',
        label: '크기 한도',
        spec: '5 MB maximum, 2 MB recommended.',
        // Sources also quote 2 MB as a hard ceiling for index.html with 5 MB
        // applying to a ZIP total. Meta takes no ZIP, so the safe reading is a
        // 5 MB failure with a warning past the 2 MB figure everyone repeats.
        run: ({ size }) => {
          const mb = (size / 1048576).toFixed(2) + ' MB';
          if (size > 5 * 1024 * 1024) return fail(mb + ' — 5 MB 초과');
          if (size > 2 * 1024 * 1024) return warn(mb + ' — 권장 2 MB 초과 (5 MB 한도는 충족)');
          return pass(mb);
        },
      },
      {
        id: 'single-file',
        label: '단일 HTML 파일',
        spec: 'Exactly one file — no ZIP, no folder, no linked assets directory.',
        run: ({ kind }) => kind === 'zip'
          ? fail('ZIP은 받지 않습니다 — 단일 HTML 파일이어야 합니다')
          : pass('단일 HTML'),
      },
      {
        id: 'no-mraid',
        label: 'MRAID 미사용',
        spec: 'MRAID format is prohibited. Meta does not use the MRAID standard.',
        // The one network where mraid is a rejection reason rather than the
        // required integration — the exact inverse of Unity, AppLovin and
        // ironSource. A creative built around mraid.open() is the wrong shape.
        run: ({ code }) => {
          const n = (code.match(/\bmraid\b/g) || []).length;
          return n === 0 ? pass('mraid 호출 없음')
            : fail(`mraid 참조 ${n}건 — Meta는 MRAID 소재를 반려합니다`);
        },
      },
      {
        id: 'cta-fb',
        label: 'CTA가 FbPlayableAd.onCTAClick() 호출',
        spec: 'Use FbPlayableAd.onCTAClick() when a person interacts with the call to action.',
        run: ({ code }) => {
          if (!/FbPlayableAd\s*(?:\.|\[\s*["'])\s*onCTAClick/.test(code))
            return fail('FbPlayableAd.onCTAClick() 호출이 없습니다');
          const m = /InstallFullGame\s*=\s*function[\s\S]{0,600}?\}/.exec(code);
          if (m && /iosLink|androidLink|window\.open|mraid/.test(m[0]))
            return fail('CTA에 다른 네트워크의 스토어 이동 로직이 남아있습니다');
          return pass('onCTAClick() 연결');
        },
      },
      {
        id: 'no-external',
        label: '외부 요청 없음',
        spec: 'Zero external requests. No dynamic asset loading through an external network.',
        run: ({ doc }) => {
          const hosts = fetchedHosts(doc);
          return hosts.length === 0
            ? pass('모든 리소스 인라인')
            : fail('외부 리소스 참조: ' + hosts.join(', '));
        },
      },
      {
        id: 'no-xhr',
        label: 'XMLHttpRequest 미사용',
        spec: 'No dynamic asset loading through an external network.',
        run: ({ code }) => /\bXMLHttpRequest\b/.test(code)
          ? fail('XMLHttpRequest 사용이 발견되었습니다')
          : pass('사용 없음'),
      },
      {
        id: 'orientation',
        label: '세로 방향 설정',
        spec: 'Vertical (Portrait) is mandatory for mobile feed placements.',
        // Read from the build's own packageConfig — "unspecified" is what an
        // untargeted Luna export carries, and it is a build setting the
        // conversion cannot fix.
        run: ({ code }) => {
          const m = /orientation\s*:\s*["']([a-z]+)["']/i.exec(code);
          if (!m) return warn('packageConfig에서 orientation을 찾을 수 없습니다');
          const v = m[1].toLowerCase();
          if (v === 'portrait') return pass('"portrait"');
          return warn(`"${v}" — 피드 노출에는 세로가 요구됩니다 (원본 빌드 설정)`);
        },
      },
    ],

    // No targeting group: DEBUG_META holds only the conversion input, so there
    // is no genuine Meta export to derive one from. The spec is unusually
    // prescriptive here — it names the CTA function and bans MRAID outright —
    // so most of what a targeting check would say is already a spec check.

    provide: ['meta'],
    hooks: [],
    runtime: [
      {
        id: 'fb-telemetry',
        label: 'Meta 분석 로깅',
        spec: 'FbPlayableAd.logGameLoad / logLevelComplete / logButtonClick 보고',
        hint: '재생하며 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 onCTAClick() 발생',
        spec: 'FbPlayableAd.onCTAClick() on the call to action.',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'first-tap',
        label: '자동 스토어 이동 없음',
        spec: 'Auto-redirect instead of explicit CTA click handling is a rejection reason.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'audio',
        label: '사용자 입력 전 무음',
        spec: 'No auto-playing audio. Sound is only allowed to start after the user has interacted.',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'no-network',
        label: '외부 네트워크 호출 없음',
        spec: 'Zero external requests.',
        hint: '재생 중 자동으로 감시합니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },

  smadex: {
    label: 'Smadex',
    source: 'Smadex Playables 규격 시트',
    static: [
      {
        id: 'size',
        label: '5 MB 미만',
        spec: 'Ensure the file is less than 5MB.',
        // "less than", not "up to" — the boundary itself fails.
        run: ({ size }) => size < 5 * 1024 * 1024
          ? pass((size / 1048576).toFixed(2) + ' MB')
          : fail((size / 1048576).toFixed(2) + ' MB — 5 MB 미만이어야 합니다'),
      },
      {
        id: 'single-file',
        label: '단일 HTML 파일',
        spec: 'You must deliver an HTML file containing all the code for the playable.',
        run: ({ kind, files, entryPath }) => {
          if (kind !== 'zip') return pass('단일 HTML');
          return files.length === 1
            ? pass('ZIP 내 ' + entryPath + ' 단일 파일')
            : fail(files.length + '개 파일 — 코드 전부를 담은 HTML 하나여야 합니다');
        },
      },
      {
        id: 'utf8',
        label: 'UTF-8 인코딩',
        spec: 'The HTML file must use only UTF-8 character encoding.',
        run: ({ doc }) => {
          const cs = doc.querySelector('head meta[charset]')?.getAttribute('charset')?.trim().toLowerCase();
          if (cs === 'utf-8') return pass('meta charset');
          const eq = doc.querySelector('head meta[http-equiv="Content-Type" i]');
          return /utf-8/i.test(eq?.getAttribute('content') || '')
            ? pass('http-equiv Content-Type') : fail('UTF-8 선언이 없습니다');
        },
      },
      {
        id: 'images-base64',
        label: '이미지 base64 인코딩',
        spec: 'Images within the playable ad should be encoded in base64.',
        // The sheet names images specifically, so a leftover base122 payload is
        // the thing to look for; compressed bundles are out of scope. Counted as
        // DOM attributes, not as text — the Luna runtime carries the string
        // `document.querySelectorAll("[data-src122]")` in its own code, and a
        // text scan reports that selector as an unconverted image.
        run: ({ doc }) => {
          const left = doc.querySelectorAll('[data-src122]').length;
          return left === 0
            ? pass('base122 이미지 없음')
            : warn(`base122 이미지 ${left}개가 남아있습니다`);
        },
      },
      {
        id: 'cta-smx',
        label: 'smxTracking.redirect() 포함',
        spec: 'The playable must include the function smxTracking.redirect() without any parameters.',
        run: ({ code }) => {
          if (/smxTracking\s*\.\s*redirect\s*\(\s*\)/.test(code)) return pass('직접 호출');
          // The bridge may dispatch by name — smxTracking[name]() — which is the
          // same call with the same empty argument list.
          if (/smxTracking\s*\[[^\]]+\]\s*\(\s*\)/.test(code) && /["']redirect["']/.test(code))
            return pass('이름 디스패치 호출');
          return fail('smxTracking.redirect() 호출을 찾을 수 없습니다');
        },
      },
      {
        id: 'events',
        label: '이벤트 3종 연결',
        spec: 'Key events: "load", "download", "complete".',
        run: ({ code }) => {
          const missing = ['load', 'download', 'complete']
            .filter(e => !new RegExp('["\']' + e + '["\']').test(code));
          return missing.length === 0
            ? pass('load · download · complete')
            : fail('누락: ' + missing.join(', '));
        },
      },
      {
        id: 'events-separate',
        label: 'download · complete 분리',
        spec: '"download" and "complete" should never trigger together.',
        // Different triggers, not merely both present: download belongs to the
        // CTA and complete to the end of the playable.
        run: ({ code }) => {
          const dl = /InstallFullGame[\s\S]{0,400}?["']download["']/.test(code);
          const cp = /GameEnded[\s\S]{0,400}?["']complete["']/.test(code);
          if (dl && cp) return pass('CTA / 게임 종료로 분리');
          if (!dl && !cp) return warn('두 이벤트의 발화 지점을 확인할 수 없습니다');
          return warn(`${dl ? 'complete' : 'download'} 의 발화 지점을 확인할 수 없습니다`);
        },
      },
      {
        id: 'no-line-comments',
        label: 'script 안 한 줄 주석 없음',
        spec: 'There must be no single-line comments (//) inside the JavaScript tag.',
        // Counted at line starts only. A minified line never begins with "//",
        // so URLs, regex literals and string contents cannot register — a plain
        // text scan for "//" reports around a hundred of those.
        run: ({ html }) => {
          let n = 0;
          for (const m of html.matchAll(/<script[^>]*>([\s\S]*?)<\/script>/gi))
            for (const line of m[1].split('\n')) if (/^\s*\/\//.test(line)) n++;
          return n === 0 ? pass('블록 주석만') : fail(`한 줄 주석 ${n}줄 — 규격이 금지합니다`);
        },
      },
      {
        id: 'no-external',
        label: '외부 리소스 참조 없음',
        spec: 'Scripts and assets should be stored so that they can be accessed from any device.',
        run: ({ doc }) => {
          const hosts = fetchedHosts(doc);
          return hosts.length === 0
            ? pass('외부 참조 없음')
            : fail('외부 리소스 참조: ' + hosts.join(', '));
        },
      },
    ],

    // No targeting group: DEBUG_SMADEX holds only the conversion input. The
    // spec sheet is prescriptive enough that the checks above already cover
    // what a targeting group would ask.

    provide: ['smadex'],
    hooks: [],
    runtime: [
      {
        id: 'smx-load',
        label: 'load 이벤트 보고',
        spec: 'This event must be present to indicate that the ad has loaded correctly.',
        hint: '빌드가 실행되면 자동으로 확인됩니다',
      },
      {
        id: 'cta',
        label: 'CTA 탭 시 redirect() 발생',
        spec: 'smxTracking.redirect() for redirection to app store.',
        hint: '미리보기에서 설치/CTA 버튼을 눌러야 확인됩니다',
      },
      {
        id: 'smx-exclusive',
        label: 'download · complete 동시 발화 없음',
        spec: '"download" and "complete" should never trigger together.',
        hint: '두 이벤트가 모두 발생하면 판정됩니다',
      },
      {
        id: 'first-tap',
        label: '자동 스토어 이동 없음',
        spec: '첫 입력 전에 CTA가 발생하면 안 됩니다.',
        hint: 'CTA가 발생하면 판정됩니다',
      },
      {
        id: 'noerror',
        label: '코드 오류 없음',
        spec: '실행 중 발생한 JS 오류 · 미처리 rejection',
        hint: '재생 중 자동으로 감시합니다',
      },
    ],
  },
};

export function rulesFor(networkKey) {
  return RULES[networkKey] || null;
}
